/**
 * Notification / maintenance top-banner helpers.
 *
 * Copy and visibility are driven by PM processing settings:
 * - NOTIFICATION_BANNER_ENABLED
 * - NOTIFICATION_BANNER_TITLE
 * - NOTIFICATION_BANNER_MESSAGE
 * - NOTIFICATION_BANNER_POPUP_MESSAGE (Learn More tooltip)
 *
 * Settings are persisted to appStorage at login (KeycloakPostLogin) and can be
 * re-fetched on /home refresh via loadNotificationBannerSettings().
 */
import HttpClient from '../../../libs/utils/httpClient';
import { getKey, setKey } from '../../../libs/utils/storageManager';

const PARTNER_PORTAL_ROOTS = new Set(['leads-home', 'leads', 'lead', 'customers']);

/** Partner portal route prefixes — banner sync runs for all of these via PartnerRoutes */
export const isPartnerPortalPath = (pathname?: string): boolean => {
    const root = pathname?.split('/').filter(Boolean)[0];
    return PARTNER_PORTAL_ROOTS.has(root ?? '');
};

/** Carousel slide id — used by Home, DashboardAccountHome, and FxTopBannerView */
export const MAINTENANCE_BANNER_ID = 'maintenance_banner';

/** Key passed in topBannerData; resolved to <MaintenanceBanner /> in FxTopBannerController */
export const MAINTENANCE_BANNER_COMPONENT = 'maintenanceBanner';

/** PM settings API request — only the four notification-banner settings */
const NOTIFICATION_SETTINGS_REQUEST = {
    criteria: {
        filters: [
            {
                operator: 'in',
                key: 'settingName',
                values: [
                    'NOTIFICATION_BANNER_ENABLED',
                    'NOTIFICATION_BANNER_TITLE',
                    'NOTIFICATION_BANNER_MESSAGE',
                    'NOTIFICATION_BANNER_POPUP_MESSAGE',
                ],
            },
        ],
    },
};

/** Coerce appStorage values to string; PM may return boolean or string */
const readSetting = (key: string): string => {
    const value = getKey(key);
    if (value === null || value === undefined) {
        return '';
    }
    return String(value);
};

const getPmSettingValue = (data: any, settingName: string): string | undefined => {
    const value = data?.resources?.find((x: any) => x.settingName === settingName);
    return value?.settingValue ?? value?.defaultValue;
};

/** True after PM settings have been fetched and written to appStorage this session */
export const areNotificationBannerSettingsLoaded = (): boolean =>
    readSetting('notificationBannerSettingsLoaded').trim().toLowerCase() === 'true';

export const isNotificationBannerEnabled = (): boolean =>
    readSetting('notificationBannerEnabled').trim().toLowerCase() === 'true';

/**
 * Gate for showing the banner — requires both loaded settings and ENABLED=true.
 * Prevents a flash of empty banner content while settings are still loading on reload.
 */
export const shouldShowNotificationBanner = (): boolean =>
    areNotificationBannerSettingsLoaded() && isNotificationBannerEnabled();

export const getNotificationBannerTitle = (): string =>
    readSetting('notificationBannerTitle');

export const getNotificationBannerMessage = (): string =>
    readSetting('notificationBannerMessage');

export const getNotificationBannerPopupMessage = (): string =>
    readSetting('notificationBannerPopupMessage');

/** Called at the start of login so stale "loaded" state cannot show the banner early */
export const clearNotificationBannerSettingsLoaded = (): void => {
    setKey('notificationBannerSettingsLoaded', 'false');
};

/** Writes all four PM notification settings to appStorage and marks them as loaded */
export const applyNotificationBannerSettings = (pmSettings: any): void => {
    const notificationBannerEnabled = getPmSettingValue(pmSettings, 'NOTIFICATION_BANNER_ENABLED') ?? 'false';
    const notificationBannerTitle = getPmSettingValue(pmSettings, 'NOTIFICATION_BANNER_TITLE') ?? '';
    const notificationBannerMessage = getPmSettingValue(pmSettings, 'NOTIFICATION_BANNER_MESSAGE') ?? '';
    const notificationBannerPopupMessage = getPmSettingValue(pmSettings, 'NOTIFICATION_BANNER_POPUP_MESSAGE') ?? '';

    setKey('notificationBannerEnabled', notificationBannerEnabled);
    setKey('notificationBannerTitle', notificationBannerTitle);
    setKey('notificationBannerMessage', notificationBannerMessage);
    setKey('notificationBannerPopupMessage', notificationBannerPopupMessage);
    setKey('notificationBannerSettingsLoaded', 'true');
};

/**
 * Fetches notification settings on /home refresh when login did not run
 * (e.g. isUserRefreshed path) but the banner may still need current PM values.
 */
export const loadNotificationBannerSettings = async (): Promise<void> => {
    const httpClient = HttpClient.getClient();
    const response = await httpClient.post('programManager/getProgramManagerSetting', NOTIFICATION_SETTINGS_REQUEST);
    applyNotificationBannerSettings(response?.data);
};

/**
 * Strips maintenance slides from carousel data.
 * Used when restoring persisted Redux topBannerData so a stale maintenance
 * slide does not flash before fresh PM settings are available.
 */
export const filterMaintenanceBannerFromCarousel = (bannerData: any[] = []): any[] =>
    bannerData.filter((item) => item?.id !== MAINTENANCE_BANNER_ID && item?.component !== MAINTENANCE_BANNER_COMPONENT);
